/**
 * Central AutoMapa analytics utility (Milestone 15).
 * UI components should call these helpers — not provider-specific APIs directly.
 */
import { resolveEventCategory as resolveLegacyCategory } from "../constants/buyerAnalyticsConstants";
import { appendBuyerAnalyticsEvent } from "../utils/buyerAnalyticsEventStore";
import { ingestBuyerAnalyticsEvent } from "../services/automapBuyerAnalyticsApi";
import {
  getAttributionSessionId,
  getAttributionVisitorId,
} from "../utils/campaignAttribution";
import { getPageAttributionContext } from "../utils/pageAttributionContext";
import { buildAttributionContext } from "./attribution";
import { resolveAutomapEventCategory } from "./events";
import { dispatchAnalyticsProviders } from "./analyticsProvider";
import { sanitizeAnalyticsPayload } from "./sanitize";
import { mergeIntlAnalyticsPayload } from "../intl/analyticsContext.js";
import { scheduleNonBlocking } from "../utils/scheduleNonBlocking";
import {
  buildChecklistAnalyticsPayload,
  buildDealerAnalyticsPayload,
  buildDealerContactAnalyticsPayload,
  buildFairPriceAnalyticsPayload,
  buildLeadAnalyticsPayload,
  buildListingAnalyticsPayload,
  buildRecommendationAnalyticsPayload,
  buildRiskAnalyticsPayload,
  buildSearchAnalyticsPayload,
} from "./eventPayloads";

export { sanitizeAnalyticsPayload, buildAttributionContext };
export {
  buildListingAnalyticsPayload,
  buildDealerAnalyticsPayload,
  buildSearchAnalyticsPayload,
  buildFairPriceAnalyticsPayload,
  buildRiskAnalyticsPayload,
  buildDealerContactAnalyticsPayload,
  buildRecommendationAnalyticsPayload,
  buildChecklistAnalyticsPayload,
  buildLeadAnalyticsPayload,
} from "./eventPayloads";
export * from "./events";

function randomEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function resolveCategory(eventName) {
  return resolveAutomapEventCategory(eventName) || resolveLegacyCategory(eventName);
}

function baseSessionContext() {
  const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
  const page = getPageAttributionContext(pagePath);
  return {
    sessionId: getAttributionSessionId(),
    visitorId: getAttributionVisitorId(),
    pagePath: page.pagePath,
    pageType: page.pageType,
    listingId: page.listingId || null,
    dealerId: page.dealerId || null,
    guideSlug: page.guideSlug || null,
  };
}

export function trackEvent(eventName, payload = {}) {
  try {
    if (!eventName) return null;

    const session = baseSessionContext();
    const enriched = sanitizeAnalyticsPayload(
      mergeIntlAnalyticsPayload({
        ...session,
        ...payload,
        pagePath: payload.pagePath || session.pagePath,
        pageType: payload.pageType || session.pageType,
        attributionContext: payload.attributionContext || buildAttributionContext({
          sourcePage: payload.sourcePage,
          sourceComponent: payload.sourceComponent,
          sourceAction: payload.sourceAction,
        }),
      })
    );

    const event = {
      id: randomEventId(),
      eventName,
      eventCategory: resolveCategory(eventName),
      sessionId: enriched.sessionId,
      visitorId: enriched.visitorId,
      listingId: enriched.listingId || null,
      dealerId: enriched.dealerId || null,
      pagePath: enriched.pagePath,
      pageType: enriched.pageType,
      sourceComponent: enriched.sourceComponent || null,
      sourceAction: enriched.sourceAction || null,
      attributionContext: enriched.attributionContext,
      eventPayload: enriched,
      createdAt: new Date().toISOString(),
    };

    appendBuyerAnalyticsEvent(event);
    dispatchAnalyticsProviders(eventName, enriched);
    ingestBuyerAnalyticsEvent(event).catch(() => {});

    return event;
  } catch {
    return null;
  }
}

/** Non-blocking analytics for hot paths (search results, impressions). */
export function scheduleTrackEvent(eventName, payload = {}) {
  scheduleNonBlocking(() => {
    trackEvent(eventName, payload);
  });
}

export function trackPageView(pageContext = {}) {
  const utm = typeof window !== "undefined"
    ? Object.fromEntries(
        [...new URLSearchParams(window.location.search).entries()].filter(([key]) =>
          key.toLowerCase().startsWith("utm_")
        )
      )
    : {};

  return trackEvent("page_view", {
    ...pageContext,
    sourceComponent: pageContext.sourceComponent || "page_router",
    sourceAction: "page_view",
    pageTitle: typeof document !== "undefined" ? document.title : pageContext.pageTitle,
    routeName: pageContext.routeName || pageContext.pagePath,
    canonicalUrl: pageContext.canonicalUrl || null,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    ...utm,
  });
}

export function trackVehicleEvent(eventName, listing, extra = {}) {
  return trackEvent(eventName, buildListingAnalyticsPayload(listing, extra));
}

export function trackSearchEvent(eventName, searchParams, extra = {}) {
  return trackEvent(eventName, buildSearchAnalyticsPayload(searchParams, extra));
}

export function trackDealerContactEvent(eventName, listing, dealer = null, extra = {}) {
  return trackEvent(eventName, buildDealerContactAnalyticsPayload(listing, dealer, extra));
}

export function trackChecklistEvent(eventName, item, listing = null, extra = {}) {
  return trackEvent(eventName, buildChecklistAnalyticsPayload(listing, item, extra));
}

export function trackRecommendationAnalyticsEvent(eventName, sourceListing, recommendedListing, extra = {}) {
  return trackEvent(eventName, buildRecommendationAnalyticsPayload(sourceListing, recommendedListing, extra));
}

export function trackFairPriceAnalyticsEvent(eventName, listing, extra = {}) {
  return trackEvent(eventName, buildFairPriceAnalyticsPayload(listing, extra));
}

export function trackRiskAnalyticsEvent(eventName, listing, extra = {}) {
  return trackEvent(eventName, buildRiskAnalyticsPayload(listing, extra));
}

export function trackLeadAnalyticsEvent(eventName, lead = {}, extra = {}) {
  return trackEvent(eventName, buildLeadAnalyticsPayload(lead, extra));
}

export function trackDealerAnalyticsEvent(eventName, dealer, extra = {}) {
  return trackEvent(eventName, buildDealerAnalyticsPayload(dealer, extra));
}
